import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";
import { wrapText } from "../ChartUtil";
import ChartTooltip, { getTooltipHTML } from "../ChartTooltip";
import Loader from "../../Loader/Loader";
import GraphDownloadButton from "../../Button/GraphDownloadButton/GraphDownloadButton";
import AboutChartToggle from "../../AboutChartToggle/AboutChartToggle";
import DataNotFound from "../../DataNotFound/DataNotFound";
import ErrorBoundary from "../../ErrorBoundary/ErrorBoundary";

const MultiBarChartInner = React.memo(
  ({
    chartData,
    chartConfig,
    keys,
    chartType,
    title,
    notes,
    colorArray = [],
    wrapInOneLine = false,
  }) => {
    const svgRef = useRef();
    const wrapperRef = useRef();
    const tooltipRef = useRef(null);
    const [dimensions, setDimensions] = useState({ width: 900, height: 650 });
    const [isLoading, setIsLoading] = useState(true);
    const [activeKeys, setActiveKeys] = useState(null);
    const [data, setData] = useState(null);

    useEffect(() => {
      if (chartData) {
        setData(() => {
          return chartData?.map((item, index) => ({
            label: item[chartConfig.x_field],
            countDetails: item[chartConfig.value_field],
          }));
        });

        setActiveKeys(() => [chartConfig.value_field]);
      } else {
        // API error, wrong response shape, or missing config — clear data so we show DataNotFound
        setData([]);
      }
    }, [chartData, chartConfig]);

    useEffect(() => {
      setIsLoading(true);
      if (data) {
        setIsLoading(false);
      }
    }, [data]);

    // Resize Observer
    useEffect(() => {
      const resizeObserver = new ResizeObserver((entries) => {
        if (!entries || !entries.length) return;
        const { width } = entries[0].contentRect;
        setDimensions({ width, height: 650 });
      });

      resizeObserver.observe(wrapperRef.current);
      return () => resizeObserver.disconnect();
    }, []);

    // Chart
    useEffect(() => {
      if (!data) return;

      const { width, height } = dimensions;
      const margin = { top: 40, right: 20, bottom: 120, left: 60 };
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = height - margin.top - margin.bottom;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      const g = svg
        .attr("width", width)
        .attr("height", height)
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      // -----------------------
      // Scales
      // -----------------------
      const x0 = d3
        .scaleBand()
        .domain(data.map((d) => d.label))
        .range([0, innerWidth]);
      //   .padding(0.1);

      const x1 = d3
        .scaleBand()
        .domain(activeKeys)
        .range([0, x0.bandwidth()])
        .padding(0.5);

      const maxValue =
        chartType === "percentage"
          ? 100
          : d3.max(data, (d) => d.countDetails.value);

      const y = d3
        .scaleLinear()
        .domain([0, maxValue])
        .nice()
        .range([innerHeight, 0]);

      // const color = d3.scaleOrdinal().domain(keys).range(d3.schemeTableau10);
      const color = d3
        .scaleOrdinal()
        .domain(data.map((d) => d.label))
        //   .range(d3.schemeTableau10);
        .range([
          ...colorArray,
          // "#B97878",
          // "#78B978",
          // "#78C0D9",
          // "#D978B9",
          // "#78D9C0",
          // "#D9B978",
          // "#9A78D9",
          // "#D9788A",
          // "#78A9D9",
          // "#A9D978",
          // ...d3.schemeTableau10,
        ]);

      // -----------------------
      // Grid Lines
      // -----------------------

      const yGrid = d3.axisLeft(y).tickSize(-innerWidth).tickFormat("");

      svg
        .append("g")
        .attr("class", "y-grid")
        .attr("transform", `translate(${margin.left},${margin.top})`)
        .call(yGrid);

      svg.select(".y-grid").lower();

      svg.selectAll(".y-grid line").attr("stroke", "#DFE2E4");

      svg.selectAll(".y-grid path").remove();

      // -----------------------
      // Axes
      // -----------------------
      const xAxis = g
        .append("g")
        .attr("transform", `translate(0,${innerHeight})`)
        .call(d3.axisBottom(x0))
        .selectAll("text")
        //   .attr("transform", "rotate(-40)")
        .style("text-anchor", "center")
        .style("font-size", "15px")
        .call(
          wrapText,
          x0.bandwidth() - 15,
          wrapInOneLine ? 1 : 4,
          wrapInOneLine ? false : true,
        );

      g.append("g")
        .call(
          d3
            .axisLeft(y)
            .tickFormat(
              (d) =>
                `${d.toLocaleString("en-US")}${chartType === "percentage" ? "%" : ""}`,
            ),
        )
        .style("font-size", "15px");

      // -----------------------
      // Bars
      // -----------------------
      const groups = g
        .selectAll(".label-group")
        .data(data)
        .enter()
        .append("g")
        .attr("transform", (d) => `translate(${x0(d.label)},0)`);

      const bars = groups
        .selectAll("rect")
        .data((d) =>
          activeKeys.map((key) => ({
            key,
            label: d.label,
            value: d[key]?.value,
            activeQuantity:
              chartType === "percentage" ? d[key]?.percentage : d[key]?.value,
            percentage: d[key]?.percentage,
          })),
        )
        .enter()
        .append("rect")
        .attr("x", (d) => x1(d.key))
        .attr("width", x1.bandwidth())
        .attr("y", innerHeight)
        .attr("height", 0)
        .attr("fill", (d) => color(d.label))
        .on("mousemove", (event, d) => {
          const wrapperRect = wrapperRef.current.getBoundingClientRect();
          const tooltip = d3.select(tooltipRef.current);

          // set content first, then measure — otherwise height/width are stale
          tooltip
            .style("opacity", 0)
            .html(
              getTooltipHTML({
                Label: d.label,
                Value: d.value,
                Percentage: d.percentage ? `${d.percentage}%` : null,
              }),
            );

          const { width, height } = tooltip.node().getBoundingClientRect();

          let x = event.clientX - wrapperRect.left + 12;
          let y = event.clientY - wrapperRect.top - height - 10;

          // keep tooltip inside the wrapper bounds
          if (x + width > wrapperRect.width) x -= width + 24;
          if (y < 0) y = event.clientY - wrapperRect.top + 10;

          tooltip
            .style("opacity", 1)
            .style("left", `${x}px`)
            .style("top", `${y}px`);
        })
        .on("mouseleave", () => {
          // tooltip.style("opacity", 0)
          d3.select(tooltipRef.current).style("opacity", 0);
        });

      // Animate Bars

      bars
        // .transition()
        // .duration(900)
        // .ease(d3.easeCubicOut)
        .attr("y", (d) => y(d.activeQuantity))
        .attr("height", (d) => innerHeight - y(d.activeQuantity));

      // Labels (middle or top)

      const labels = groups
        .selectAll("g.label")
        .data((d) =>
          activeKeys.map((key) => ({
            key,
            value: d[key]?.value,
            percentage: d[key]?.percentage,
            activeQuantity:
              chartType === "percentage" ? d[key]?.percentage : d[key]?.value,
          })),
        )
        .enter()
        .append("g")
        .attr("class", "label")
        .attr("transform", (d) => {
          const barHeight = innerHeight - y(d.activeQuantity);
          const yPos =
            barHeight > 0
              ? y(d.activeQuantity) - (d.percentage ? 9 : 9)
              : y(d.activeQuantity) + barHeight / 2;
          return `translate(${x1(d.key) + x1.bandwidth() / 2}, ${yPos})`;
        });

      labels
        .append("text")
        .text((d) => d.value?.toLocaleString("en-US"))
        .attr("dy", "-0.2em")
        .attr("fill", (d) =>
          innerHeight - y(d.activeQuantity) < 25 ? "#000" : "#000",
        )
        .attr("text-anchor", "middle")
        .style("font-size", "13px")
        .style("font-weight", 600);
    }, [data, keys, activeKeys, dimensions]);

    const toggleKey = (key) => {
      setActiveKeys((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
      );
    };

    return (
      <div
        ref={wrapperRef}
        style={{ width: "100%", minWidth: "768px", position: "relative" }}
      >
        <div className="">
          {notes && (
            <div className="mb-3">
              <AboutChartToggle notes={notes} />
            </div>
          )}
          <div className="">
            <GraphDownloadButton
              svg={svgRef.current}
              fileName={title}
              jsonData={data}
            />
          </div>
        </div>

        {keys.length > 1 && (
          <div style={{ display: "flex", gap: "16px", marginBottom: "10px" }}>
            {keys.map((key) => (
              <label key={key} style={{ cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={activeKeys.includes(key)}
                  onChange={() => toggleKey(key)}
                  style={{ marginRight: "6px" }}
                />
                {key}
              </label>
            ))}
          </div>
        )}

        {isLoading ? (
          <Loader />
        ) : chartData?.length > 0 ? (
          <svg ref={svgRef} />
        ) : (
          <DataNotFound />
        )}
        <ChartTooltip ref={tooltipRef} />
      </div>
    );
  },
);

const MultiBarChart = (props) => (
  <ErrorBoundary>
    <MultiBarChartInner {...props} />
  </ErrorBoundary>
);

export default MultiBarChart;
